(function () {
  const SEARCH_PAGE_SCRIPT_URL = document.currentScript?.src || '';
  const DEFAULT_SEARCH_IMAGE_URL = SEARCH_PAGE_SCRIPT_URL
    ? new URL('mbi-default-collection-image.png', SEARCH_PAGE_SCRIPT_URL).href
    : '';
  const SEARCH_ROOT_SELECTOR =
    'body:is(.template-search, [class*="template-collection"]) .boost-sd__filter-product-list';
  const GRID_DENSITY_STORAGE_KEY = 'mbiProductGridDensity.v1';
  const NON_PRODUCT_GRID_DENSITY_STORAGE_KEY = 'mbiNonProductGridDensity.v1';
  const GRID_COLUMN_CLASS_PATTERN =
    /^boost-sd__product-list-(?:grid--\d+-col|undefined-col)$/;
  let scheduled = false;
  let observedProductList = null;
  let gridDensityOffset = readGridDensityOffset();
  let nonProductGridDensityOffset = readNonProductGridDensityOffset();
  let searchMetadataQuery = '';
  let searchMetadataPromise = null;
  let contentFeedQuery = '';
  const contentFeedPromises = new Map();
  const observedCards = new WeakSet();
  const cardResizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const borderBox = entry.borderBoxSize?.[0];
      updateCardScale(
        entry.target,
        borderBox?.inlineSize || entry.target.getBoundingClientRect().width
      );
    });
  });
  const productListResizeObserver = new ResizeObserver((entries) => {
    if (entries.some((entry) => entry.target.isConnected)) {
      scheduleEnhancement();
    }
  });
  const productListClassObserver = new MutationObserver(scheduleEnhancement);

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function readGridDensityOffset() {
    try {
      const stored = Number.parseInt(
        window.localStorage.getItem(GRID_DENSITY_STORAGE_KEY),
        10
      );
      return Number.isFinite(stored) ? clamp(stored, -2, 1) : 0;
    } catch (_error) {
      return 0;
    }
  }

  function saveGridDensityOffset() {
    try {
      window.localStorage.setItem(
        GRID_DENSITY_STORAGE_KEY,
        String(gridDensityOffset)
      );
    } catch (_error) {
      /* Storage can be unavailable in privacy modes. The in-page control still
         works for the current render, so there is nothing else to recover. */
    }
  }

  function readNonProductGridDensityOffset() {
    try {
      const stored = Number.parseInt(
        window.localStorage.getItem(NON_PRODUCT_GRID_DENSITY_STORAGE_KEY),
        10
      );
      return Number.isFinite(stored) ? clamp(stored, -2, 1) : 0;
    } catch (_error) {
      return 0;
    }
  }

  function saveNonProductGridDensityOffset() {
    try {
      window.localStorage.setItem(
        NON_PRODUCT_GRID_DENSITY_STORAGE_KEY,
        String(nonProductGridDensityOffset)
      );
    } catch (_error) {
      /* The control remains usable for the current page when storage is blocked. */
    }
  }

  function interpolate(start, end, progress) {
    return start + ((end - start) * progress);
  }

  function formatPixels(value) {
    return `${Math.round(value * 100) / 100}px`;
  }

  function updateCardScale(item, measuredWidth) {
    const width = measuredWidth || item.getBoundingClientRect().width;
    if (width < 1) return;

    const progress = Math.min(Math.max((width - 160) / 140, 0), 1);
    const textProgress = Math.pow(progress, 0.7);
    const paddingTop = interpolate(0, 24, progress);
    const paddingSide = interpolate(10, 20, progress);
    const paddingBottom = interpolate(10, 18, progress);
    const contentGap = interpolate(7, 16, progress);
    const imageBleed = paddingSide * (1 - progress);

    item.style.setProperty(
      '--mbi-fluid-card-padding',
      `${formatPixels(paddingTop)} ${formatPixels(paddingSide)} ${formatPixels(paddingBottom)}`
    );
    item.style.setProperty('--mbi-fluid-card-gap', `${formatPixels(contentGap)} 0`);
    item.style.setProperty('--mbi-fluid-card-image-total-bleed', formatPixels(imageBleed * 2));
    item.style.setProperty('--mbi-fluid-card-image-negative-bleed', formatPixels(-imageBleed));
    item.style.setProperty(
      '--mbi-fluid-card-title-gap',
      formatPixels(interpolate(2, 4, progress))
    );
    item.style.setProperty(
      '--mbi-fluid-card-price-padding',
      formatPixels(interpolate(4, 10, progress))
    );
    item.style.setProperty(
      '--mbi-fluid-card-price-bottom-gap',
      formatPixels(interpolate(1, 4, progress))
    );
    item.style.setProperty(
      '--mbi-fluid-card-rating-top-gap',
      formatPixels(interpolate(2, 6, progress))
    );
    item.style.setProperty(
      '--mbi-fluid-card-rating-bottom-gap',
      formatPixels(interpolate(8, 17, progress))
    );
    item.style.setProperty(
      '--mbi-fluid-card-action-gap',
      formatPixels(interpolate(6, 8, progress))
    );
    item.style.setProperty(
      '--mbi-boost-card-text-size',
      formatPixels(interpolate(13.5, 18, textProgress))
    );
    item.style.setProperty(
      '--mbi-fluid-card-price-size',
      formatPixels(interpolate(12.8, 18, textProgress))
    );
    item.style.setProperty(
      '--mbi-boost-button-text-size',
      formatPixels(interpolate(12, 16, textProgress))
    );
    /* "Save £X" pill: same ramp shape as the card heading (13.5→18), scaled
       trimmed twice on 02/09 (14 → 13 → 12 top end) so the longest strings
       — "SAVE £22.98" — stay on the price line. These two numbers are the
       only tuning knobs for the pill. */
    item.style.setProperty(
      '--mbi-fluid-card-saving-size',
      formatPixels(interpolate(8, 12, textProgress))
    );
  }

  function observeCardScales(root) {
    root.querySelectorAll('.boost-sd__product-item').forEach((item) => {
      if (observedCards.has(item)) return;

      observedCards.add(item);
      updateCardScale(item);
      cardResizeObserver.observe(item);
    });
  }

  function ensureHeaderResultTabs(root) {
    if (!document.body.classList.contains('template-search')) return;

    const nativeTabs = Array.from(
      root.querySelectorAll('.boost-sd__search-result-toolbar-panel')
    );
    const heroInner = document.querySelector('.mbi-search-hero__inner');
    if (!nativeTabs.length || !heroInner) return;

    let slot = heroInner.querySelector('.mbi-search-hero__tabs');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'mbi-search-hero__tabs';
      slot.setAttribute('role', 'group');
      slot.setAttribute('aria-label', 'Search result types');
      heroInner.appendChild(slot);

      slot.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mbi-result-tab-index]');
        if (!button) return;

        const currentRoot = document.querySelector(SEARCH_ROOT_SELECTOR);
        const currentTabs = currentRoot?.querySelectorAll(
          '.boost-sd__search-result-toolbar-panel'
        );
        const targetIndex = Number(button.dataset.mbiResultTabIndex);
        const contentSubtype = button.dataset.mbiContentSubtype;
        if (contentSubtype && currentRoot) {
          currentRoot.dataset.mbiContentSubtype = contentSubtype;
        }

        const targetTab = currentTabs?.[targetIndex];
        const targetIsActive =
          targetTab?.getAttribute('aria-selected') === 'true' ||
          targetTab?.classList.contains('boost-sd__search-result-toolbar-panel--active');
        if (targetTab && !targetIsActive) {
          targetTab.click();
        } else {
          scheduleEnhancement();
        }
      });
    }

    const activeNativeIndex = nativeTabs.findIndex((nativeTab) =>
      nativeTab.getAttribute('aria-selected') === 'true' ||
      nativeTab.classList.contains('boost-sd__search-result-toolbar-panel--active')
    );
    const activeNativeTab = nativeTabs[activeNativeIndex];
    const activeLabel = activeNativeTab?.textContent.trim().toLowerCase() || '';
    const contentTabIndex = nativeTabs.findIndex((nativeTab) =>
      /blogs?\s*&\s*pages?/i.test(nativeTab.textContent)
    );
    const allowedButtonKeys = new Set();

    const ensureButton = (key, index, contentSubtype = '') => {
      allowedButtonKeys.add(key);
      let button = slot.querySelector(`[data-mbi-result-tab-key="${key}"]`);
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'mbi-search-hero__tab';
        button.dataset.mbiResultTabKey = key;
        slot.appendChild(button);
      }
      button.dataset.mbiResultTabIndex = String(index);
      if (contentSubtype) {
        button.dataset.mbiContentSubtype = contentSubtype;
      } else {
        delete button.dataset.mbiContentSubtype;
      }
      return button;
    };

    nativeTabs.forEach((nativeTab, index) => {
      const text = nativeTab.textContent.trim();
      const selected =
        nativeTab.getAttribute('aria-selected') === 'true' ||
        nativeTab.classList.contains('boost-sd__search-result-toolbar-panel--active');
      const accessibleLabel = nativeTab.getAttribute('aria-label') || text;

      if (index === contentTabIndex) {
        const selectedSubtype = root.dataset.mbiContentSubtype || 'blogs';
        ['blogs', 'pages'].forEach((subtype) => {
          const button = ensureButton(`${index}-${subtype}`, index, subtype);
          const provisionalLabel = subtype === 'blogs' ? 'Blogs' : 'Pages';
          if (!button.dataset.mbiCountReady && button.textContent !== provisionalLabel) {
            button.textContent = provisionalLabel;
          }
          button.setAttribute('aria-label', provisionalLabel);
          button.setAttribute('aria-pressed', String(selected && subtype === selectedSubtype));
          button.classList.toggle(
            'mbi-search-hero__tab--active',
            selected && subtype === selectedSubtype
          );
        });
        return;
      }

      const button = ensureButton(String(index), index);

      if (button.textContent !== text) button.textContent = text;
      if (button.getAttribute('aria-label') !== accessibleLabel) {
        button.setAttribute('aria-label', accessibleLabel);
      }
      const resultCount = Number.parseInt(text.match(/\((\d+)\)\s*$/)?.[1], 10);
      const hideEmptyOptionalTab = index > 0 && resultCount === 0;
      button.hidden = hideEmptyOptionalTab;
      button.setAttribute('aria-hidden', String(hideEmptyOptionalTab));
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('mbi-search-hero__tab--active', selected);
    });

    const activeType = activeLabel.startsWith('products')
      ? 'products'
      : activeLabel.startsWith('collections')
        ? 'collections'
        : 'content';

    root.classList.remove(
      'mbi-search-tab--products',
      'mbi-search-tab--collections',
      'mbi-search-tab--content'
    );
    root.classList.add(`mbi-search-tab--${activeType}`);
    root.dataset.mbiSearchResultType = activeType;
    if (activeType === 'content' && !root.dataset.mbiContentSubtype) {
      root.dataset.mbiContentSubtype = 'blogs';
    }

    slot.querySelectorAll('[data-mbi-result-tab-key]').forEach((button) => {
      if (!allowedButtonKeys.has(button.dataset.mbiResultTabKey)) {
        button.remove();
      }
    });

    if (contentTabIndex >= 0) {
      void Promise.all([
        getContentFeed('blogs', 1),
        getContentFeed('pages', 1)
      ]).then(([blogFeed, pageFeed]) => {
        if (!blogFeed || !pageFeed) return;
        const counts = { blogs: blogFeed.total, pages: pageFeed.total };
        let changed = false;
        ['blogs', 'pages'].forEach((subtype) => {
          const button = slot.querySelector(
            `[data-mbi-content-subtype="${subtype}"]`
          );
          if (!button) return;
          const label = `${subtype === 'blogs' ? 'Blogs' : 'Pages'} (${counts[subtype]})`;
          const shouldHide = counts[subtype] === 0;
          if (button.textContent !== label) {
            button.textContent = label;
            changed = true;
          }
          if (button.hidden !== shouldHide) {
            button.hidden = shouldHide;
            button.setAttribute('aria-hidden', String(shouldHide));
            changed = true;
          }
          button.dataset.mbiCountReady = 'true';
        });

        const selectedSubtype = root.dataset.mbiContentSubtype || 'blogs';
        if (counts[selectedSubtype] === 0) {
          const fallbackSubtype = counts.blogs > 0 ? 'blogs' : counts.pages > 0 ? 'pages' : '';
          if (fallbackSubtype && fallbackSubtype !== selectedSubtype) {
            root.dataset.mbiContentSubtype = fallbackSubtype;
            changed = true;
          }
        }
        if (changed) scheduleEnhancement();
      }).catch(() => {
        /* Keep Boost's provisional combined content tab usable if the dedicated
           content endpoint is temporarily unavailable. */
      });
    }

    document.body.classList.add('mbi-search-tabs-in-hero');
    document.querySelector('[data-mbi-search-hero]')?.classList.add(
      'mbi-search-hero--tabs-ready'
    );
  }

  function normaliseResultPath(value) {
    try {
      return new URL(value, window.location.origin).pathname.replace(
        /^\/[a-z]{2}-[a-z]{2}(?=\/)/i,
        ''
      );
    } catch (_error) {
      return value || '';
    }
  }

  function plainTextFromHtml(value) {
    if (!value) return '';
    const template = document.createElement('template');
    template.innerHTML = value;
    return (template.content.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function trimCardDescription(value, maximumLength = 210) {
    const text = plainTextFromHtml(value)
      .replace(/\s*Target keywords:.*$/i, '')
      .trim();
    if (text.length <= maximumLength) return text;

    const shortened = text.slice(0, maximumLength + 1);
    const lastSpace = shortened.lastIndexOf(' ');
    return `${shortened.slice(0, lastSpace > 0 ? lastSpace : maximumLength).trim()}…`;
  }

  function formatSearchDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    try {
      return new Intl.DateTimeFormat(
        document.documentElement.lang || 'en-GB',
        { day: 'numeric', month: 'long', year: 'numeric' }
      ).format(date);
    } catch (_error) {
      return '';
    }
  }

  function resizeShopifyImage(url, width) {
    if (!url) return '';
    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', String(width));
      return imageUrl.href;
    } catch (_error) {
      return url;
    }
  }

  function getSearchMetadata() {
    if (!document.body.classList.contains('template-search')) {
      return Promise.resolve(new Map());
    }

    const query = new URLSearchParams(window.location.search).get('q')?.trim() || '';
    if (!query) return Promise.resolve(new Map());
    if (searchMetadataPromise && searchMetadataQuery === query) {
      return searchMetadataPromise;
    }

    searchMetadataQuery = query;
    const localeRoot = window.Shopify?.routes?.root || '/';
    const endpoint = new URL(`${localeRoot}search/suggest.json`, window.location.origin);
    endpoint.searchParams.set('q', query);
    endpoint.searchParams.set('resources[type]', 'article,page,collection');
    endpoint.searchParams.set('resources[limit]', '10');
    endpoint.searchParams.set('resources[limit_scope]', 'each');
    endpoint.searchParams.set('resources[options][fields]', 'title,body');

    searchMetadataPromise = window.fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search metadata request failed: ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const results = payload?.resources?.results || {};
        const metadata = new Map();
        ['articles', 'pages', 'collections'].forEach((type) => {
          (results[type] || []).forEach((item) => {
            metadata.set(normaliseResultPath(item.url), { ...item, type });
            if (item.handle) metadata.set(`handle:${item.handle}`, { ...item, type });
            if (item.title) {
              metadata.set(`title:${item.title.trim().toLowerCase()}`, { ...item, type });
            }
          });
        });
        return metadata;
      })
      .catch(() => new Map());

    return searchMetadataPromise;
  }

  function getContentFeed(subtype, page = 1) {
    const query = new URLSearchParams(window.location.search).get('q')?.trim() || '';
    if (!query) return Promise.resolve(null);
    if (contentFeedQuery !== query) {
      contentFeedQuery = query;
      contentFeedPromises.clear();
    }

    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const key = `${subtype}:${safePage}`;
    if (contentFeedPromises.has(key)) return contentFeedPromises.get(key);

    const localeRoot = window.Shopify?.routes?.root || '/';
    const endpoint = new URL(`${localeRoot}search`, window.location.origin);
    endpoint.searchParams.set('q', query);
    endpoint.searchParams.set('type', subtype === 'pages' ? 'page' : 'article');
    endpoint.searchParams.set('view', 'mbi-content');
    endpoint.searchParams.set('page', String(safePage));

    const request = window.fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    }).then((response) => {
      if (!response.ok) throw new Error(`Content search request failed: ${response.status}`);
      return response.text();
    }).then((html) => {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const feed = documentFragment.querySelector('[data-mbi-content-feed]');
      const items = feed?.querySelector('[data-mbi-content-feed-items]');
      if (!feed || !items) throw new Error('Content search feed missing');
      return {
        subtype,
        total: Number.parseInt(feed.dataset.total, 10) || 0,
        currentPage: Number.parseInt(feed.dataset.currentPage, 10) || 1,
        pageCount: Number.parseInt(feed.dataset.pageCount, 10) || 1,
        itemsHtml: items.innerHTML
      };
    }).catch(() => null);

    contentFeedPromises.set(key, request);
    return request;
  }

  function ensureSearchCardImage(card, item) {
    let imageWrapper = card.querySelector(
      '.boost-sd__search-result-panel-content-item-image'
    );
    if (!imageWrapper) {
      imageWrapper = document.createElement('div');
      imageWrapper.className = 'boost-sd__search-result-panel-content-item-image';
      card.prepend(imageWrapper);
    }

    let image = imageWrapper.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      imageWrapper.appendChild(image);
    }

    const featuredImage = item?.featured_image;
    const existingSource = image.currentSrc || image.getAttribute('src') || '';
    const source = featuredImage?.url || item?.image || existingSource || DEFAULT_SEARCH_IMAGE_URL;
    if (!source) return;

    const source360 = resizeShopifyImage(source, 360);
    const source600 = resizeShopifyImage(source, 600);
    const source900 = resizeShopifyImage(source, 900);
    image.src = source600;
    image.srcset = `${source360} 360w, ${source600} 600w, ${source900} 900w`;
    image.sizes = '(min-width: 750px) 30vw, calc(100vw - 3.2rem)';
    image.alt = featuredImage?.alt || item?.title || image.alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = Number(featuredImage?.width) || 1000;
    image.height = Number(featuredImage?.height) || 1000;
  }

  function enrichSearchCard(card, item, activeType) {
    if (activeType === 'content') {
      const href = card.getAttribute('href') || '';
      const isPage = item?.type === 'pages' ||
        (item?.type !== 'articles' && !href.includes('/blogs/'));
      card.dataset.mbiSearchContentType = isPage ? 'pages' : 'blogs';
    }
    if (card.classList.contains('mbi-search-content-card--ready')) return;

    ensureSearchCardImage(card, item);
    let text = card.querySelector('.boost-sd__search-result-panel-content-item-text');
    if (!text) {
      text = document.createElement('div');
      text.className = 'boost-sd__search-result-panel-content-item-text';
      card.appendChild(text);
    }

    const title = text.querySelector('.boost-sd__search-result-panel-content-item-title');
    const isArticle = item?.type === 'articles';
    const dateText = isArticle ? formatSearchDate(item?.published_at) : '';
    const description = trimCardDescription(
      item?.summary_html || item?.body || ''
    );

    if (
      activeType === 'content' &&
      card.dataset.mbiSearchContentType !== 'pages' &&
      !text.querySelector('.mbi-search-content-card__type')
    ) {
      const href = card.getAttribute('href') || '';
      const isPage = item?.type === 'pages' || (item?.type !== 'articles' && !href.includes('/blogs/'));
      const type = document.createElement('span');
      type.className = `mbi-search-content-card__type mbi-search-content-card__type--${isPage ? 'page' : 'blog'}`;
      type.textContent = isPage ? 'Page' : 'Blog';
      text.insertBefore(type, text.firstChild);
    }

    if (dateText && !text.querySelector('.mbi-search-content-card__date')) {
      const date = document.createElement('time');
      date.className = 'mbi-search-content-card__date';
      date.dateTime = item.published_at;
      date.textContent = dateText;
      text.insertBefore(date, title || text.firstChild);
    }

    if (description && !text.querySelector('.mbi-search-content-card__description')) {
      const summary = document.createElement('p');
      summary.className = 'mbi-search-content-card__description';
      summary.textContent = description;
      text.appendChild(summary);
    }

    if (!text.querySelector('.mbi-search-content-card__cta')) {
      const cta = document.createElement('span');
      cta.className = 'mbi-search-content-card__cta';
      cta.textContent = activeType === 'collections'
        ? 'View collection'
        : card.dataset.mbiSearchContentType === 'pages'
          ? 'View page'
          : 'Read more';
      cta.setAttribute('aria-hidden', 'true');
      text.appendChild(cta);
    }

    card.classList.add('mbi-search-content-card--ready');
  }

  function getContentPage(root, subtype) {
    const key = subtype === 'pages' ? 'mbiPagesPage' : 'mbiBlogsPage';
    return Math.max(1, Number.parseInt(root.dataset[key], 10) || 1);
  }

  function setContentPage(root, subtype, page) {
    const key = subtype === 'pages' ? 'mbiPagesPage' : 'mbiBlogsPage';
    root.dataset[key] = String(Math.max(1, Number.parseInt(page, 10) || 1));
  }

  function createContentPageButton(label, page, currentPage, disabled = false) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mbi-search-content-pagination__button';
    button.textContent = label;
    button.dataset.mbiContentPage = String(page);
    button.disabled = disabled;
    if (page === currentPage && /^\d+$/.test(label)) {
      button.classList.add('mbi-search-content-pagination__button--active');
      button.setAttribute('aria-current', 'page');
    }
    return button;
  }

  function renderContentPagination(root, container, panel, feed) {
    let pagination = container.querySelector(':scope > .mbi-search-content-pagination');
    if (!pagination) {
      pagination = document.createElement('nav');
      pagination.className = 'mbi-search-content-pagination';
      pagination.setAttribute('aria-label', 'Search results pages');
      panel.after(pagination);
      pagination.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mbi-content-page]');
        if (!button || button.disabled) return;
        const subtype = root.dataset.mbiContentSubtype || 'blogs';
        setContentPage(root, subtype, button.dataset.mbiContentPage);
        void renderContentFeed(root, panel, true);
      });
    }

    pagination.replaceChildren();
    pagination.hidden = feed.pageCount <= 1;
    if (pagination.hidden) return;

    pagination.appendChild(
      createContentPageButton('Previous', feed.currentPage - 1, feed.currentPage, feed.currentPage <= 1)
    );
    const firstPage = Math.max(1, Math.min(feed.currentPage - 2, feed.pageCount - 4));
    const lastPage = Math.min(feed.pageCount, firstPage + 4);
    for (let page = firstPage; page <= lastPage; page += 1) {
      pagination.appendChild(
        createContentPageButton(String(page), page, feed.currentPage)
      );
    }
    pagination.appendChild(
      createContentPageButton('Next', feed.currentPage + 1, feed.currentPage, feed.currentPage >= feed.pageCount)
    );
  }

  async function renderContentFeed(root, panel, force = false) {
    if (root.dataset.mbiSearchResultType !== 'content') return false;
    const subtype = root.dataset.mbiContentSubtype || 'blogs';
    const page = getContentPage(root, subtype);
    const query = new URLSearchParams(window.location.search).get('q')?.trim() || '';
    const renderKey = `${query}:${subtype}:${page}`;
    if (!force && panel.dataset.mbiContentRenderKey === renderKey) return true;

    panel.classList.remove('mbi-search-content-panel--ready');
    panel.setAttribute('aria-busy', 'true');
    const feed = await getContentFeed(subtype, page);
    if (!feed) {
      panel.removeAttribute('aria-busy');
      return false;
    }
    if (
      root.dataset.mbiSearchResultType !== 'content' ||
      (root.dataset.mbiContentSubtype || 'blogs') !== subtype ||
      getContentPage(root, subtype) !== page
    ) {
      return true;
    }

    const template = document.createElement('template');
    template.innerHTML = feed.itemsHtml;
    panel.replaceChildren(template.content.cloneNode(true));
    panel.dataset.mbiContentRenderKey = renderKey;
    panel.dataset.mbiSearchEnrichment = 'ready';
    panel.removeAttribute('aria-busy');

    const container = panel.closest('.boost-sd-container-search-page');
    if (container) renderContentPagination(root, container, panel, feed);
    panel.classList.add('mbi-search-content-panel--ready');
    applyNonProductGrid(root);
    if (force) panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return true;
  }

  async function enrichNonProductResults(root) {
    if (!document.body.classList.contains('template-search')) return;
    const activeType = root.dataset.mbiSearchResultType;
    if (!['collections', 'content'].includes(activeType)) return;

    const panel = root.querySelector(
      '.boost-sd-container-search-page > .boost-sd__search-result-panel-content'
    );
    if (!panel) return;
    if (activeType === 'content' && await renderContentFeed(root, panel)) return;
    const cards = Array.from(
      panel.querySelectorAll('.boost-sd__search-result-panel-content-item')
    );
    if (!cards.length || cards.every((card) =>
      card.classList.contains('mbi-search-content-card--ready')
    )) {
      applyContentSubtype(root, panel);
      panel.classList.add('mbi-search-content-panel--ready');
      return;
    }
    if (panel.dataset.mbiSearchEnrichment === 'loading') return;

    panel.dataset.mbiSearchEnrichment = 'loading';
    panel.classList.remove('mbi-search-content-panel--ready');
    const metadata = await getSearchMetadata();
    cards.forEach((card) => {
      const path = normaliseResultPath(card.getAttribute('href'));
      const handle = path.split('/').filter(Boolean).pop() || '';
      const title = card.querySelector(
        '.boost-sd__search-result-panel-content-item-title'
      )?.textContent.trim().toLowerCase() || '';
      const item = metadata.get(path) ||
        metadata.get(`handle:${handle}`) ||
        metadata.get(`title:${title}`);
      enrichSearchCard(card, item, activeType);
    });
    applyContentSubtype(root, panel);
    panel.dataset.mbiSearchEnrichment = 'ready';
    panel.classList.add('mbi-search-content-panel--ready');
  }

  function enhanceNoProductResults(root) {
    const noResults = root.querySelector('.boost-sd__no-search-result-page');
    if (!noResults) return;

    const searchTips = noResults.querySelector(
      '.boost-sd__no-search-result-page-search-tip'
    );
    if (searchTips) searchTips.hidden = true;

    const trendingProducts = noResults.querySelector(
      '.boost-sd__no-search-result-page-popular-products'
    );
    if (trendingProducts) trendingProducts.hidden = true;

    if (!noResults.querySelector('.mbi-search-zero__rabbit')) {
      const rabbit = document.createElement('img');
      rabbit.className = 'mbi-search-zero__rabbit';
      rabbit.src = 'https://makebelieveideas.co.uk/cdn/shop/files/Rabbit_Search_Magnifying_Glass.png';
      rabbit.alt = 'Rabbit looking through a magnifying glass';
      rabbit.loading = 'eager';
      rabbit.decoding = 'async';
      rabbit.width = 500;
      rabbit.height = 690;
      noResults.prepend(rabbit);
    }

    const empty = noResults.querySelector(
      '.boost-sd__no-search-result-page-empty'
    );
    if (!empty || empty.dataset.mbiZeroEnhanced === 'true') return;

    const lineBreak = empty.querySelector('br');
    if (!lineBreak) {
      empty.dataset.mbiZeroEnhanced = 'true';
      return;
    }

    const title = document.createElement('h2');
    title.className = 'mbi-search-zero__title';
    while (empty.firstChild && empty.firstChild !== lineBreak) {
      title.appendChild(empty.firstChild);
    }
    lineBreak.remove();

    const divider = document.createElement('span');
    divider.className = 'mbi-search-zero__divider';
    divider.setAttribute('aria-hidden', 'true');

    const message = document.createElement('p');
    message.className = 'mbi-search-zero__message';
    while (empty.firstChild) message.appendChild(empty.firstChild);

    empty.append(title, divider, message);
    empty.dataset.mbiZeroEnhanced = 'true';
  }

  function applyContentSubtype(root, panel) {
    if (root.dataset.mbiSearchResultType !== 'content') return;
    const subtype = root.dataset.mbiContentSubtype || 'blogs';
    panel.querySelectorAll('.boost-sd__search-result-panel-content-item').forEach((card) => {
      const cardType = card.dataset.mbiSearchContentType ||
        (card.getAttribute('href')?.includes('/blogs/') ? 'blogs' : 'pages');
      card.hidden = cardType !== subtype;
    });

    document.querySelectorAll(
      '.mbi-search-hero__tab[data-mbi-content-subtype]'
    ).forEach((button) => {
      const selected = button.dataset.mbiContentSubtype === subtype;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('mbi-search-hero__tab--active', selected);
    });
  }

  function getGridLimits(listWidth) {
    return listWidth < 560
      ? { minimum: 1, maximum: 3 }
      : { minimum: listWidth < 800 ? 1 : 2, maximum: 4 };
  }

  function getDefaultGridColumns(listWidth) {
    if (listWidth < 560) return 2;
    if (listWidth < 800) return 3;
    return 4;
  }

  function getGridState(list) {
    const listWidth = list.getBoundingClientRect().width;
    const limits = getGridLimits(listWidth);
    const defaultColumns = getDefaultGridColumns(listWidth);
    const columns = clamp(
      defaultColumns + gridDensityOffset,
      limits.minimum,
      limits.maximum
    );

    return { columns, defaultColumns, ...limits };
  }

  function updateGridDensityControls(root, state) {
    const controls = root.matches?.('.mbi-grid-density')
      ? root
      : root.querySelector('.mbi-grid-density');
    if (!controls) return;

    const fewer = controls.querySelector('[data-mbi-grid-density="fewer"]');
    const more = controls.querySelector('[data-mbi-grid-density="more"]');
    const status = controls.querySelector('.mbi-grid-density__status');

    if (fewer) fewer.disabled = state.columns <= state.minimum;
    if (more) more.disabled = state.columns >= state.maximum;
    const statusText = `${state.columns} columns`;
    if (status && status.textContent !== statusText) {
      status.textContent = statusText;
    }

    controls.dataset.columns = String(state.columns);
  }

  function applyAdaptiveProductGrid(root) {
    const list = root.querySelector('.boost-sd__product-list');
    if (!list) return;

    if (observedProductList !== list) {
      productListResizeObserver.disconnect();
      productListClassObserver.disconnect();
      productListResizeObserver.observe(list);
      productListClassObserver.observe(list, {
        attributes: true,
        attributeFilter: ['class']
      });
      observedProductList = list;
    }

    const state = getGridState(list);
    const desiredClass = `boost-sd__product-list-grid--${state.columns}-col`;
    const currentColumnClasses = Array.from(list.classList).filter(
      (className) => GRID_COLUMN_CLASS_PATTERN.test(className)
    );

    if (
      currentColumnClasses.length !== 1 ||
      currentColumnClasses[0] !== desiredClass ||
      !list.classList.contains('mbi-product-grid--managed')
    ) {
      currentColumnClasses.forEach((className) => {
        list.classList.remove(className);
      });
      list.classList.add(desiredClass, 'mbi-product-grid--managed');
    }
    list.dataset.mbiColumns = String(state.columns);

    updateGridDensityControls(root, state);
  }

  function changeGridDensity(root, direction) {
    const list = root.querySelector('.boost-sd__product-list');
    if (!list) return;

    const state = getGridState(list);
    const target = clamp(
      state.columns + direction,
      state.minimum,
      state.maximum
    );
    if (target === state.columns) return;

    gridDensityOffset = clamp(
      gridDensityOffset + (target - state.columns),
      -2,
      1
    );
    saveGridDensityOffset();
    applyAdaptiveProductGrid(root);
  }

  function createGridDensityButton(direction, symbol, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mbi-grid-density__button';
    button.dataset.mbiGridDensity = direction;
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
    button.textContent = symbol;
    return button;
  }

  function ensureGridDensityControls(root) {
    const toolbar = root.querySelector('.boost-sd__toolbar-top-mobile');
    if (!toolbar) return;

    let controls = toolbar.querySelector('.mbi-grid-density');
    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'mbi-grid-density';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Columns');

      const label = document.createElement('span');
      label.className = 'mbi-grid-density__label';
      label.textContent = 'Columns:';

      const status = document.createElement('span');
      status.className = 'mbi-grid-density__status visually-hidden';
      status.setAttribute('aria-live', 'polite');

      const buttonGroup = document.createElement('span');
      buttonGroup.className = 'mbi-grid-density__controls';
      buttonGroup.append(
        createGridDensityButton('fewer', '\u2212', 'Fewer columns'),
        createGridDensityButton('more', '+', 'More columns')
      );
      controls.append(label, buttonGroup, status);
      toolbar.prepend(controls);

      controls.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mbi-grid-density]');
        if (!button) return;

        changeGridDensity(
          root,
          button.dataset.mbiGridDensity === 'more' ? 1 : -1
        );
      });
    }
  }

  function getNonProductGridState(panel) {
    const width = panel.getBoundingClientRect().width;
    if (window.matchMedia('(max-width: 749px)').matches) {
      return { columns: 1, defaultColumns: 1, minimum: 1, maximum: 1 };
    }

    const defaultColumns = clamp(Math.floor((width + 30) / 300), 2, 4);
    const maximum = 4;
    const minimum = 2;
    const columns = clamp(
      defaultColumns + nonProductGridDensityOffset,
      minimum,
      maximum
    );
    return { columns, defaultColumns, minimum, maximum };
  }

  function applyNonProductGrid(root) {
    const panel = root.querySelector(
      '.boost-sd-container-search-page > .boost-sd__search-result-panel-content'
    );
    if (!panel) return;

    const isPages =
      root.dataset.mbiSearchResultType === 'content' &&
      root.dataset.mbiContentSubtype === 'pages';
    if (isPages) {
      panel.style.removeProperty('--mbi-nonproduct-columns');
      return;
    }

    const state = getNonProductGridState(panel);
    panel.style.setProperty('--mbi-nonproduct-columns', String(state.columns));
    const controls = root.querySelector('.mbi-nonproduct-grid-density');
    if (controls) updateGridDensityControls(controls, state);
  }

  function changeNonProductGridDensity(root, direction) {
    const panel = root.querySelector(
      '.boost-sd-container-search-page > .boost-sd__search-result-panel-content'
    );
    if (!panel) return;
    const state = getNonProductGridState(panel);
    const target = clamp(
      state.columns + direction,
      state.minimum,
      state.maximum
    );
    if (target === state.columns) return;

    nonProductGridDensityOffset = clamp(
      nonProductGridDensityOffset + (target - state.columns),
      -2,
      1
    );
    saveNonProductGridDensityOffset();
    applyNonProductGrid(root);
  }

  function ensureNonProductGridControls(root) {
    const container = root.querySelector('.boost-sd-container-search-page');
    const panel = container?.querySelector(':scope > .boost-sd__search-result-panel-content');
    if (!container || !panel) return;

    let toolbar = container.querySelector(':scope > .mbi-nonproduct-toolbar');
    if (!toolbar) {
      toolbar = document.createElement('div');
      toolbar.className = 'mbi-nonproduct-toolbar';

      const controls = document.createElement('div');
      controls.className = 'mbi-grid-density mbi-nonproduct-grid-density';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Columns');

      const label = document.createElement('span');
      label.className = 'mbi-grid-density__label';
      label.textContent = 'Columns:';

      const status = document.createElement('span');
      status.className = 'mbi-grid-density__status visually-hidden';
      status.setAttribute('aria-live', 'polite');

      const buttonGroup = document.createElement('span');
      buttonGroup.className = 'mbi-grid-density__controls';
      buttonGroup.append(
        createGridDensityButton('fewer', '\u2212', 'Fewer columns'),
        createGridDensityButton('more', '+', 'More columns')
      );
      controls.append(label, buttonGroup, status);
      toolbar.appendChild(controls);
      container.insertBefore(toolbar, panel);

      controls.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mbi-grid-density]');
        if (!button) return;
        changeNonProductGridDensity(
          root,
          button.dataset.mbiGridDensity === 'more' ? 1 : -1
        );
      });
    }

    const shouldShow =
      root.dataset.mbiSearchResultType === 'collections' ||
      (root.dataset.mbiSearchResultType === 'content' &&
        root.dataset.mbiContentSubtype === 'blogs');
    toolbar.hidden = !shouldShow;
    applyNonProductGrid(root);
  }

  const REVERSIBLE_SORTS = {
    title: {
      label: 'Title',
      preferredDirection: 'ascending',
      ascending: 'A to Z',
      descending: 'Z to A'
    },
    price: {
      label: 'Price',
      preferredDirection: 'ascending',
      ascending: 'lowest to highest',
      descending: 'highest to lowest'
    },
    created: {
      label: 'Date Published',
      preferredDirection: 'descending',
      ascending: 'oldest to newest',
      descending: 'newest to oldest'
    },
    'percent-sale': {
      label: 'Discount',
      preferredDirection: 'descending',
      ascending: 'least to most',
      descending: 'most to least'
    },
    ratings: {
      label: 'Ratings',
      preferredDirection: 'descending',
      ascending: 'worst to best',
      descending: 'best to worst'
    },
    /* Boost can expose these two native types as numbered extra sorts in its
       storefront markup. Keep both representations mapped to the same labels. */
    'number-extra-sort1': {
      label: 'Discount',
      preferredDirection: 'descending',
      ascending: 'least to most',
      descending: 'most to least'
    },
    'number-extra-sort2': {
      label: 'Ratings',
      preferredDirection: 'descending',
      ascending: 'worst to best',
      descending: 'best to worst'
    }
  };

  function getReversibleSortDetails(option) {
    const value = option.dataset.value || '';
    const match = value.match(/^(.*)-(ascending|descending)$/);
    if (!match || !REVERSIBLE_SORTS[match[1]]) return null;

    return {
      key: match[1],
      direction: match[2],
      definition: REVERSIBLE_SORTS[match[1]]
    };
  }

  function getSortingOptionTarget(option) {
    return option.querySelector('button, [role="button"]') || option;
  }

  function compactReversibleSorting(sorting, sortingValue) {
    sorting.querySelector('.mbi-price-sort-option')?.remove();
    sorting.parentElement?.querySelector(
      ':scope > .mbi-price-sort-direction'
    )?.remove();

    const groups = {};
    let activeDetails = null;

    sorting.querySelectorAll('.boost-sd__sorting-option').forEach((option) => {
      option.classList.remove(
        'mbi-native-price-sort-option',
        'mbi-sort-option--secondary',
        'mbi-sort-option--primary',
        'mbi-sort-option--active'
      );

      const details = getReversibleSortDetails(option);
      if (!details) return;

      groups[details.key] ||= [];
      groups[details.key].push({ option, ...details });

      if (
        option.getAttribute('aria-selected') === 'true' ||
        option.classList.contains('boost-sd__sorting-option--active')
      ) {
        activeDetails = { option, ...details };
      }
    });

    Object.values(groups).forEach((items) => {
      const ascending = items.find((item) => item.direction === 'ascending');
      const descending = items.find((item) => item.direction === 'descending');
      if (!ascending || !descending) return;

      const primary = items.find(
        (item) => item.direction === item.definition.preferredDirection
      );
      if (!primary) return;
      const secondary = primary === ascending ? descending : ascending;
      const primaryTarget = getSortingOptionTarget(primary.option);

      primary.option.classList.add('mbi-sort-option--primary');
      secondary.option.classList.add('mbi-sort-option--secondary');
      if (primaryTarget.textContent !== primary.definition.label) {
        primaryTarget.textContent = primary.definition.label;
      }
      primaryTarget.setAttribute(
        'aria-label',
        `Sort by ${primary.definition.label}`
      );

      if (activeDetails?.key === primary.key) {
        primary.option.classList.add('mbi-sort-option--active');
      }
    });

    const directionButton = sorting.parentElement?.querySelector(
      ':scope > .mbi-sort-direction'
    );
    const activeGroup = activeDetails ? groups[activeDetails.key] : null;
    const activeAscending = activeGroup?.find(
      (item) => item.direction === 'ascending'
    );
    const activeDescending = activeGroup?.find(
      (item) => item.direction === 'descending'
    );

    if (!activeDetails || !activeAscending || !activeDescending) {
      directionButton?.remove();
      return;
    }

    const definition = activeDetails.definition;
    if (sortingValue.textContent.trim() !== definition.label) {
      sortingValue.textContent = definition.label;
    }

    let toggle = directionButton;
    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'mbi-sort-direction';
      toggle.innerHTML = [
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">',
        '<path d="M12 19V5M6 11l6-6 6 6"/>',
        '</svg>'
      ].join('');
      sorting.after(toggle);
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        /* Boost does not honour a synthetic click on its visually hidden
           reverse option. Apply that native sort value through the URL instead;
           this preserves all active filter/search parameters and remains stable
           when Boost replaces its React-owned sorting DOM. */
        const targetValue = toggle.dataset.mbiTargetSort;
        if (!targetValue) return;

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('sort', targetValue);
        nextUrl.searchParams.delete('page');
        window.location.assign(nextUrl.toString());
      });
    }

    const isAscending = activeDetails.direction === 'ascending';
    const currentLabel = definition[activeDetails.direction];
    const nextDirection = isAscending ? 'descending' : 'ascending';
    const nextLabel = definition[nextDirection];

    toggle.classList.toggle('mbi-sort-direction--descending', !isAscending);
    toggle.setAttribute(
      'aria-label',
      `${definition.label}: ${currentLabel}. Change to ${nextLabel}`
    );
    toggle.title = `Change to ${nextLabel}`;
    toggle.dataset.mbiTargetSort = `${activeDetails.key}-${nextDirection}`;
  }

  function labelDefaultSorting(root) {
    root.querySelectorAll('.boost-sd__sorting').forEach((sorting) => {
      const button = sorting.querySelector('.boost-sd__sorting-button');
      const sortingValue = sorting.querySelector('.boost-sd__sorting-value');
      if (!button || !sortingValue) return;

      if (!sortingValue.textContent.trim()) {
        sortingValue.textContent = 'Relevance';
      }

      let label = sorting.querySelector(':scope > .mbi-sorting-label');
      if (!label) {
        label = document.createElement('span');
        label.className = 'mbi-sorting-label';
        label.setAttribute('aria-hidden', 'true');
        sorting.insertBefore(label, button);
      }
      if (label.textContent !== 'Sort:') label.textContent = 'Sort:';

      Array.from(button.childNodes).forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        if (!/sort\s*by\s*:?/i.test(node.textContent || '')) return;

        const remainder = (node.textContent || '').replace(
          /sort\s*by\s*:?/i,
          ''
        );
        if (remainder.trim()) {
          node.textContent = remainder;
        } else {
          node.remove();
        }
      });

      compactReversibleSorting(sorting, sortingValue);
      button.setAttribute(
        'aria-label',
        `Sort: ${sortingValue.textContent.trim()}`
      );
    });
  }

  /* Market commerce guard.
     html.mbi-ecommerce-off is set synchronously by
     snippets/mbi-market-config.liquid in <head>, so it is readable before this
     asset's first pass — no load-order assumption. Read per call, not once at
     load, so no pass can ever run market-blind. */
  function isEcommerceOff() {
    return document.documentElement.classList.contains('mbi-ecommerce-off');
  }

  function isSoldOutButton(button) {
    const isPending =
      button.classList.contains('mbi-boost-cart-pending') ||
      button.getAttribute('aria-busy') === 'true';

    if (isPending) return false;

    const label = [
      button.getAttribute('aria-label') || '',
      button.getAttribute('title') || '',
      button.textContent || ''
    ].join(' ').toLowerCase();

    return button.disabled ||
      label.includes('sold out') ||
      label.includes('out of stock') ||
      label.includes('unavailable');
  }

  function getProductDetails(item) {
    const link = item.querySelector('a[href*="/products/"]');
    const title = item.querySelector('.boost-sd__product-title')?.textContent.trim();

    return {
      url: link?.getAttribute('href') || '',
      title: title || 'product'
    };
  }

  function setResponsiveViewLabel(element) {
    if (element.querySelector('.mbi-boost-view-cta__word')) return;

    element.replaceChildren(document.createTextNode('View'));

    const word = document.createElement('span');
    word.className = 'mbi-boost-view-cta__word';
    word.textContent = '\u00a0Product';
    element.appendChild(word);
  }

  function normaliseViewLabels(root) {
    root.querySelectorAll('.mbi-boost-commerce-view-cta').forEach(
      setResponsiveViewLabel
    );
  }

  function normaliseQuickViewButtons(root) {
    root.querySelectorAll('.boost-sd__product-item').forEach((item) => {
      const quickView = item.querySelector(
        '.boost-sd__button--quick-view, .boost-sd__btn-quick-view'
      );
      const row = item.querySelector(
        isEcommerceOff()
          ? '.mbi-boost-market-cta'
          : '.mbi-boost-commerce-cta'
      );
      if (!quickView || !row) return;

      quickView.classList.add('mbi-boost-quick-view');
      const product = getProductDetails(item);
      quickView.setAttribute('aria-label', `Quick view ${product.title}`);
      quickView.setAttribute('title', 'Quick view');

      const finalAction = row.querySelector(
        '.boost-sd__button.boost-sd__button--select-option, .mbi-boost-amazon-cta'
      );
      if (finalAction) {
        if (
          quickView.parentElement !== row ||
          quickView.nextElementSibling !== finalAction
        ) {
          row.insertBefore(quickView, finalAction);
        }
      } else if (quickView.parentElement !== row) {
        row.appendChild(quickView);
      }
    });
  }

  function normaliseSaleFilterLabels(scope) {
    scope.querySelectorAll(
      '[data-filteroptionid="pf_ps_on_sale"] .boost-sd__filter-option-item-label, ' +
      '[data-filteroptionid="pf_ps_on_sale"] .boost-sd__filter-option-item-button--with-checkbox'
    ).forEach((label) => {
      const textNode = [...label.childNodes].find((node) =>
        node.nodeType === Node.TEXT_NODE && /under\s*100\s*%/i.test(node.textContent || '')
      );

      if (textNode) {
        textNode.textContent = textNode.textContent.replace(
          /under\s*100\s*%/gi,
          'On Sale'
        );
      } else if (/under\s*100\s*%/i.test(label.textContent || '')) {
        /* Some Boost releases wrap the value in a nested span. Preserve any
           count/checkbox siblings and change only the matching descendant. */
        const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
        let node = walker.nextNode();

        while (node) {
          if (/under\s*100\s*%/i.test(node.textContent || '')) {
            node.textContent = node.textContent.replace(
              /under\s*100\s*%/gi,
              'On Sale'
            );
            break;
          }
          node = walker.nextNode();
        }
      } else {
        return;
      }

      label.closest('button')?.setAttribute('aria-label', 'Filter by On Sale');
    });
  }

  function syncMobileFilterLayer(scope) {
    const mobileFilter = scope.querySelector('#boost-sd_filter-tree-mobile');
    const isOpen = Boolean(
      mobileFilter &&
      mobileFilter.getClientRects().length &&
      getComputedStyle(mobileFilter).display !== 'none' &&
      getComputedStyle(mobileFilter).visibility !== 'hidden'
    );

    scope.classList.toggle('mbi-search-mobile-filter-layer-open', isOpen);
  }

  function normaliseSoldOutButtons(root) {
    /* N1 (confirmed on a US grid 02/09: market 10 / commerce 10, and the
       select-option buttons already gone — this row was left ORPHANED after
       Boost customization.js stripped the button it was built around).

       In non-ecommerce markets (US/CA/AU) customization.js is the SINGLE OWNER
       of every card CTA: it builds .mbi-boost-view-cta inside
       .mbi-boost-market-cta and removes the purchasing controls. The row below
       uses a different container and guards on its OWN class
       (.mbi-boost-commerce-view-cta), so neither owner can see the other and
       every card got two View Product buttons.

       This branch is NOT an edge case in those markets: every US paired record
       is permanently availableForSale:false / inventory 0 / DENY, so Boost
       renders a sold-out button on every card and this ran every time. Per N1,
       no Sold Out treatment (disable, relabel, style) belongs there either —
       the one guard covers both concerns.

       KEEP this branch for ecommerce markets: customization.js deliberately
       does NOT build a View CTA for a sold-out card (its normaliseProductCards
       disabled-button path strips classes and never calls
       ensureCommerceActionRow), and .mbi-search-button--sold-out is added here
       and styled only in mbi-search-page.css. Deleting it would regress UK
       sold-out cards. See G23/G24. */
    if (isEcommerceOff()) return;

    root.querySelectorAll(
      '.boost-sd__button.boost-sd__button--select-option'
    ).forEach((button) => {
      if (!isSoldOutButton(button)) return;

      const item = button.closest('.boost-sd__product-item');
      if (!item) return;

      button.classList.add('mbi-search-button--sold-out');
      button.classList.remove(
        'mbi-boost-button--has-basket-icon',
        'mbi-boost-button--has-cart-icon',
        'mbi-boost-button--has-view-icon',
        'mbi-boost-button--has-view-label',
        'mbi-boost-button--in-cart'
      );
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', 'Sold out');
      button.setAttribute('title', 'Sold out');

      /* The disabled crossed-basket artwork is CSS-owned. Remove Boost's
         text/native icon while retaining the accessible name above. */
      if (button.childNodes.length) button.replaceChildren();

      let row = item.querySelector('.mbi-boost-commerce-cta');
      if (!row) {
        row = document.createElement('div');
        row.className = 'mbi-boost-commerce-cta';
        button.parentElement.insertBefore(row, button);
      }

      if (!row.querySelector('.mbi-boost-commerce-view-cta')) {
        const product = getProductDetails(item);
        if (product.url) {
          const view = document.createElement('a');
          view.className = 'mbi-boost-view-cta mbi-boost-commerce-view-cta';
          view.href = product.url;
          view.setAttribute('aria-label', `View ${product.title}`);
          view.setAttribute('title', 'View product');
          setResponsiveViewLabel(view);
          row.appendChild(view);
        }
      }

      /* Sold-out cards keep Quick View (user 06/10). customization.js only
         injects the eye via ensureCommerceActionRow, which its sold-out path
         never calls, so add Boost's own delegated quickView trigger here —
         same markup/contract as customization.js normaliseQuickViewForItem. */
      const productId = String(
        item.getAttribute('data-product-id') || item.getAttribute('id') || ''
      ).trim();
      if (productId) {
        let quickView = item.querySelector(
          '.boost-sd__button--quick-view, .boost-sd__btn-quick-view'
        );
        if (!quickView) {
          quickView = document.createElement('button');
          quickView.type = 'button';
          quickView.className =
            'boost-sd__button boost-sd__button--quick-view mbi-boost-quick-view';
          quickView.dataset.mbiQuickViewInjected = 'true';
        }
        quickView.setAttribute('data-metadata', JSON.stringify({
          action: { quickView: { productId } }
        }));
        const product = getProductDetails(item);
        quickView.setAttribute('aria-label', `Quick view ${product.title || 'product'}`);
        quickView.setAttribute('title', 'Quick view');
        if (quickView.parentElement !== row || quickView.nextElementSibling !== button) {
          row.insertBefore(quickView, button.parentElement === row ? button : null);
        }
      }

      if (button.parentElement !== row || button !== row.lastElementChild) {
        row.appendChild(button);
      }
    });
  }

  function addCollectionListScope(root) {
    if (!document.body.className.includes('template-collection')) return;

    const scope = root.closest('.shopify-section') || root.parentElement;
    scope?.classList.add('template-search', 'mbi-collection-list-scope');
  }

  function enhanceSearchPage() {
    const root = document.querySelector(SEARCH_ROOT_SELECTOR);
    if (!root) return;

    addCollectionListScope(root);
    root.classList.add('mbi-search-results');
    const scope = root.closest('.shopify-section') || root.parentElement || root;
    try {
      ensureHeaderResultTabs(root);
      void getSearchMetadata();
      void enrichNonProductResults(root);
      enhanceNoProductResults(root);
      ensureGridDensityControls(root);
      ensureNonProductGridControls(root);
      applyAdaptiveProductGrid(root);
      labelDefaultSorting(root);
      /* Filter controls are siblings of the product list, not descendants of
         it. Work from the stable Shopify section so desktop/mobile re-renders
         are both covered. */
      normaliseSaleFilterLabels(scope);
      syncMobileFilterLayer(scope);
      normaliseSoldOutButtons(root);
      normaliseQuickViewButtons(root);
      normaliseViewLabels(root);
      observeCardScales(root);
    } finally {
      /* Boost replaces card nodes during filtering and sorting. Keep each new
         card out of paint until the same frame has applied its final classes,
         action-row placement and fluid sizing. Visibility preserves the grid
         geometry, so this removes the unstyled flash without adding a reflow. */
      root.querySelectorAll('.boost-sd__product-item').forEach((item) => {
        item.classList.add('mbi-search-card--ready');
      });
      root.classList.add('mbi-search-results--ready');
    }
  }

  function scheduleEnhancement() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      enhanceSearchPage();
    });
  }

  function start() {
    scheduleEnhancement();

    let observedScope = null;
    const observer = new MutationObserver(scheduleEnhancement);
    const discoveryObserver = new MutationObserver(() => {
      if (observeSearchScope()) discoveryObserver.disconnect();
    });

    function observeSearchScope() {
      const root = document.querySelector(SEARCH_ROOT_SELECTOR);
      if (!root) return false;

      const scope = root.closest('.shopify-section') || root.parentElement || root;
      if (scope === observedScope) return true;

      observer.disconnect();
      observer.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden', 'aria-selected', 'class', 'style']
      });
      observedScope = scope;
      scheduleEnhancement();
      return true;
    }

    /* Observe the page only while waiting for Boost's root to arrive. Once it
       exists, limit mutation work to this Shopify section instead of reacting
       to cart, header, recommendations and every other page update. */
    if (!observeSearchScope()) {
      discoveryObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    window.addEventListener('resize', scheduleEnhancement, { passive: true });
  }

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }
})();
